import { useState, useCallback, useRef } from 'react'
import { useQuery, useMutation } from 'convex/react'
import { api } from '@convex/_generated/api'
import type { Id, Doc } from '@convex/_generated/dataModel'
import { compressImage, type CompressionResult } from '@/lib/imageCompression'

// Re-export Convex types for convenience
export type ConvexPropertyImage = Doc<'property_images'>
export type PropertyImageId = Id<'property_images'>
export type PropertyId = Id<'properties'>
export type StorageId = Id<'_storage'>

/**
 * Staged image for upload during property creation.
 * Tracks files that have been uploaded to storage but not yet linked to a property.
 */
export interface StagedImage {
  /** Local UUID for tracking in UI */
  id: string
  /** Convex storage ID */
  storageId: StorageId
  /** Local preview URL (blob URL) */
  previewUrl: string
  /** Original file reference */
  file: File
  /** Alt text for the image */
  altText?: string
  /** Image width in pixels */
  width?: number
  /** Image height in pixels */
  height?: number
  /** File size in bytes */
  fileSize?: number
  /** MIME type */
  mimeType?: string
  /** Compression status */
  compression?: {
    originalSize: number
    compressedSize: number
  }
}

/**
 * PropertyImage type used by components.
 * Adds computed fields and compatibility aliases for the old API shape.
 */
export interface PropertyImage {
  id: string // Maps to _id
  property_id: string
  storage_id: string
  url: string
  alt_text: string | null
  sort_order: number
  created_at: number
  // Computed field: first image (sort_order 0) is primary
  is_primary: boolean
}

/**
 * Convert a Convex property_images document to the component PropertyImage type.
 */
export function toPropertyImage(doc: ConvexPropertyImage, isPrimary?: boolean): PropertyImage {
  return {
    id: doc._id,
    property_id: doc.property_id,
    storage_id: doc.storage_id,
    url: doc.url,
    alt_text: doc.alt_text ?? null,
    sort_order: doc.sort_order,
    created_at: doc.created_at,
    is_primary: isPrimary ?? doc.sort_order === 0,
  }
}

/**
 * Hook for fetching all images for a property.
 * Uses Convex's real-time queries - data updates automatically when it changes.
 * Returns images converted to the component PropertyImage type with is_primary computed.
 */
export function usePropertyImages(propertyId: PropertyId | string | undefined) {
  const rawData = useQuery(
    api.images.listByProperty,
    propertyId ? { property_id: propertyId as PropertyId } : 'skip'
  ) as ConvexPropertyImage[] | undefined

  // Convert Convex documents to component-friendly PropertyImage type
  const data = rawData?.map((doc: ConvexPropertyImage, index: number) => toPropertyImage(doc, index === 0))

  return {
    data,
    isLoading: rawData === undefined && propertyId !== undefined,
    error: null as Error | null, // Convex throws on error
  }
}

/**
 * Hook for generating an upload URL for Convex storage.
 * Returns a mutation function that returns a URL for uploading files.
 */
export function useGenerateUploadUrl() {
  const generateMutation = useMutation(api.images.generateUploadUrl)

  const mutate = async () => {
    return await generateMutation({})
  }

  return {
    mutate,
    mutateAsync: mutate,
  }
}

/**
 * Hook for saving an uploaded image to the property_images table.
 * Call this after uploading a file to the storage URL returned by useGenerateUploadUrl.
 */
export function useSaveImage() {
  const saveMutation = useMutation(api.images.saveImage)

  const mutate = async ({
    propertyId,
    storageId,
    altText,
  }: {
    propertyId: PropertyId | string
    storageId: Id<'_storage'>
    altText?: string
  }) => {
    return await saveMutation({
      property_id: propertyId as PropertyId,
      storage_id: storageId,
      alt_text: altText,
    })
  }

  return {
    mutate,
    mutateAsync: mutate,
  }
}

/**
 * Hook for uploading an image to Convex storage with compression.
 * Combines generateUploadUrl, compression, and saveImage into a single operation.
 */
export function useUploadImage() {
  const generateUploadUrl = useMutation(api.images.generateUploadUrl)
  const saveImage = useMutation(api.images.saveImage)
  const [isPending, setIsPending] = useState(false)
  const [isCompressing, setIsCompressing] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [compressionProgress, setCompressionProgress] = useState<{
    originalSize: number
    compressedSize: number
  } | null>(null)

  const mutateAsync = useCallback(async ({
    propertyId,
    file,
    altText,
  }: {
    propertyId: PropertyId | string
    file: File
    altText?: string
  }) => {
    setIsPending(true)
    setError(null)
    setCompressionProgress(null)

    try {
      // Step 1: Compress the image
      setIsCompressing(true)
      let fileToUpload = file
      let width: number | undefined
      let height: number | undefined
      let fileSize: number | undefined
      let mimeType: string | undefined

      try {
        const result = await compressImage(file)
        fileToUpload = result.file
        width = result.width
        height = result.height
        fileSize = result.compressedSize
        mimeType = result.mimeType
        setCompressionProgress({
          originalSize: result.originalSize,
          compressedSize: result.compressedSize,
        })
      } catch (compressionError) {
        // If compression fails, use original file
        console.warn('Image compression failed, using original:', compressionError)
        fileSize = file.size
        mimeType = file.type
      }
      setIsCompressing(false)

      // Step 2: Get upload URL
      const uploadUrl = await generateUploadUrl({})

      // Step 3: Upload file to storage
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': fileToUpload.type },
        body: fileToUpload,
      })

      if (!response.ok) {
        throw new Error('Failed to upload file')
      }

      const { storageId } = await response.json()

      // Step 4: Save image record with metadata
      const imageId = await saveImage({
        property_id: propertyId as PropertyId,
        storage_id: storageId,
        alt_text: altText,
        width,
        height,
        file_size: fileSize,
        mime_type: mimeType,
      })

      return imageId
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      setError(error)
      throw error
    } finally {
      setIsPending(false)
      setIsCompressing(false)
    }
  }, [generateUploadUrl, saveImage])

  return {
    mutate: mutateAsync,
    mutateAsync,
    isPending,
    isCompressing,
    error,
    compressionProgress,
  }
}

/**
 * Hook for deleting an image.
 * Removes both the storage file and the database record.
 */
export function useDeleteImage() {
  const deleteMutation = useMutation(api.images.remove)
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const variablesRef = useRef<{ id: string; propertyId?: string } | null>(null)

  const mutateAsync = useCallback(async ({ id, propertyId }: { id: PropertyImageId | string; propertyId?: string }) => {
    setIsPending(true)
    setError(null)
    variablesRef.current = { id: id as string, propertyId }
    try {
      const result = await deleteMutation({
        id: id as PropertyImageId,
      })
      return result
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      setError(error)
      throw error
    } finally {
      setIsPending(false)
      variablesRef.current = null
    }
  }, [deleteMutation])

  return {
    mutate: mutateAsync,
    mutateAsync,
    isPending,
    error,
    variables: variablesRef.current,
  }
}

/**
 * Hook for reordering images for a property.
 * Takes an array of image IDs in the new order.
 */
export function useReorderImages() {
  const reorderMutation = useMutation(api.images.reorder)
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const mutateAsync = useCallback(async ({
    propertyId,
    imageIds,
  }: {
    propertyId: PropertyId | string
    imageIds: (PropertyImageId | string)[]
  }) => {
    setIsPending(true)
    setError(null)
    try {
      const result = await reorderMutation({
        property_id: propertyId as PropertyId,
        image_ids: imageIds as PropertyImageId[],
      })
      return result
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      setError(error)
      throw error
    } finally {
      setIsPending(false)
    }
  }, [reorderMutation])

  return {
    mutate: mutateAsync,
    mutateAsync,
    isPending,
    error,
  }
}

/**
 * Hook for updating image order (alias for backwards compatibility).
 * Maps from the old API shape to the new Convex-based shape.
 */
export function useUpdateImageOrder() {
  const reorderMutation = useMutation(api.images.reorder)
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const mutateAsync = useCallback(async ({
    items,
    propertyId,
  }: {
    items: { id: string; sort_order: number }[]
    propertyId: string
  }) => {
    setIsPending(true)
    setError(null)
    try {
      // Sort items by sort_order and extract just the IDs
      const sortedIds = items
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((item) => item.id as PropertyImageId)

      const result = await reorderMutation({
        property_id: propertyId as PropertyId,
        image_ids: sortedIds,
      })
      return result
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      setError(error)
      throw error
    } finally {
      setIsPending(false)
    }
  }, [reorderMutation])

  return {
    mutate: mutateAsync,
    mutateAsync,
    isPending,
    error,
  }
}

/**
 * Hook for updating image metadata (alt_text).
 * Note: This is a compatibility stub. The Convex schema doesn't support
 * updating image metadata after creation. This will be addressed when
 * the ImageGallery component is updated to use Convex in PRD item #10.
 * @deprecated Use the reorder mutation or delete and re-upload for changes.
 */
export function useUpdateImage() {
  const [isPending] = useState(false)
  const [error] = useState<Error | null>(null)

  const mutateAsync = useCallback(async (_args: {
    id: string
    data: { alt_text?: string }
    propertyId: string
  }) => {
    throw new Error('useUpdateImage is not implemented in Convex. Image metadata cannot be updated after creation.')
  }, [])

  return {
    mutate: mutateAsync,
    mutateAsync,
    isPending,
    error,
  }
}

/**
 * Hook for setting an image as the primary image.
 * Note: In Convex, the primary image is determined by sort_order (first image is primary).
 * Use useReorderImages to move an image to the first position.
 * This is a compatibility stub for the old API.
 * @deprecated Use useReorderImages to move an image to position 0.
 */
export function useSetImagePrimary() {
  const [isPending] = useState(false)
  const [error] = useState<Error | null>(null)
  const variablesRef = useRef<{ id: string; propertyId: string } | null>(null)

  const mutateAsync = useCallback(async (_args: { id: string; propertyId: string }) => {
    throw new Error('useSetImagePrimary is not implemented. Use useReorderImages to move an image to the first position.')
  }, [])

  return {
    mutate: mutateAsync,
    mutateAsync,
    isPending,
    error,
    variables: variablesRef.current,
  }
}

// ============================================================================
// Staged Upload Hooks (for property creation flow)
// ============================================================================

/**
 * Hook for uploading a file directly to Convex storage.
 * Returns the storageId for later association with a property.
 * Used for staged uploads during property creation.
 */
export function useUploadToStorage() {
  const generateUploadUrl = useMutation(api.images.generateUploadUrl)
  const trackPendingUpload = useMutation(api.images.trackPendingUpload)
  const [isPending, setIsPending] = useState(false)
  const [isCompressing, setIsCompressing] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const mutateAsync = useCallback(async (file: File): Promise<StagedImage> => {
    setIsPending(true)
    setError(null)

    try {
      // Generate local ID for tracking
      const localId = crypto.randomUUID()

      // Step 1: Compress the image
      setIsCompressing(true)
      let compressionResult: CompressionResult | null = null
      let fileToUpload = file

      try {
        compressionResult = await compressImage(file)
        fileToUpload = compressionResult.file
      } catch (compressionError) {
        // If compression fails, use original file
        console.warn('Image compression failed, using original:', compressionError)
      }
      setIsCompressing(false)

      // Step 2: Get upload URL
      const uploadUrl = await generateUploadUrl({})

      // Step 3: Upload file to storage
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': fileToUpload.type },
        body: fileToUpload,
      })

      if (!response.ok) {
        throw new Error('Failed to upload file')
      }

      const { storageId } = await response.json() as { storageId: StorageId }

      // Step 4: Track in pending_uploads table
      await trackPendingUpload({ storage_id: storageId })

      // Create preview URL for display
      const previewUrl = URL.createObjectURL(fileToUpload)

      const stagedImage: StagedImage = {
        id: localId,
        storageId,
        previewUrl,
        file: fileToUpload,
        width: compressionResult?.width,
        height: compressionResult?.height,
        fileSize: compressionResult?.compressedSize ?? file.size,
        mimeType: compressionResult?.mimeType ?? file.type,
        compression: compressionResult
          ? {
              originalSize: compressionResult.originalSize,
              compressedSize: compressionResult.compressedSize,
            }
          : undefined,
      }

      return stagedImage
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      setError(error)
      throw error
    } finally {
      setIsPending(false)
      setIsCompressing(false)
    }
  }, [generateUploadUrl, trackPendingUpload])

  return {
    mutate: mutateAsync,
    mutateAsync,
    isPending,
    isCompressing,
    error,
  }
}

/**
 * Hook for saving multiple staged images to a newly created property.
 * Batch operation to link all staged uploads to a property.
 */
export function useSaveImagesForNewProperty() {
  const saveImagesMutation = useMutation(api.images.saveImagesForNewProperty)
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const mutateAsync = useCallback(async ({
    propertyId,
    images,
  }: {
    propertyId: PropertyId | string
    images: StagedImage[]
  }) => {
    setIsPending(true)
    setError(null)

    try {
      const result = await saveImagesMutation({
        property_id: propertyId as PropertyId,
        images: images.map((img) => ({
          storage_id: img.storageId,
          alt_text: img.altText,
          width: img.width,
          height: img.height,
          file_size: img.fileSize,
          mime_type: img.mimeType,
        })),
      })

      // Clean up preview URLs after successful save
      images.forEach((img) => {
        if (img.previewUrl) {
          URL.revokeObjectURL(img.previewUrl)
        }
      })

      return result
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      setError(error)
      throw error
    } finally {
      setIsPending(false)
    }
  }, [saveImagesMutation])

  return {
    mutate: mutateAsync,
    mutateAsync,
    isPending,
    error,
  }
}
